import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { startRegistration } from '@simplewebauthn/browser';
import { Fingerprint, LogOut, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDateTime } from '../lib/format';
import { DataPoint, EmptyState, PageHeader, Spinner } from '../components/ui';

interface Passkey {
  id: number;
  name: string | null;
  device_type: string | null;
  backed_up: boolean;
  last_used_at: string | null;
  created_at: string;
}

const ROLE_LABELS: Record<string, string> = {
  admin: 'Yagemi Admin',
  manager: 'Khadi Manager',
  viewer: 'Khadi Leser',
};

/** Eigenes Konto: Rolle einsehen, Passkeys verwalten, abmelden. */
export function ProfilePage() {
  const auth = useAuth();
  const queryClient = useQueryClient();

  const passkeys = useQuery({
    queryKey: ['passkeys'],
    queryFn: () => api<Passkey[]>('/auth/passkeys'),
  });

  const addPasskey = useMutation({
    mutationFn: async () => {
      const { challenge_id, options } = await api<{ challenge_id: string; options: unknown }>(
        '/auth/passkey/register/options',
        { method: 'POST' },
      );
      const response = await startRegistration({ optionsJSON: options as never });
      await api('/auth/passkey/register/verify', {
        method: 'POST',
        body: { challenge_id, response, name: deviceName() },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['passkeys'] });
      toast.success('Passkey hinterlegt');
    },
    onError: (error) =>
      toast.error(
        error instanceof ApiError ? error.message : 'Die Einrichtung wurde abgebrochen',
      ),
  });

  const removePasskey = useMutation({
    mutationFn: (id: number) => api(`/auth/passkeys/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['passkeys'] });
      toast.success('Passkey entfernt');
    },
  });

  if (!auth.user) return null;

  return (
    <div className="mx-auto max-w-2xl">
      <PageHeader eyebrow="Konto" title={auth.user.name} subtitle={auth.user.email} />

      <section className="surface animate-fade-up mb-4 p-5">
        <dl className="grid grid-cols-2 gap-4">
          <DataPoint label="Rolle">{ROLE_LABELS[auth.user.role] ?? auth.user.role}</DataPoint>
          <DataPoint label="Zwei-Faktor">
            {auth.user.totp_enrolled ? 'eingerichtet' : 'nicht eingerichtet'}
          </DataPoint>
        </dl>
      </section>

      <section className="surface animate-fade-up pad" style={{ animationDelay: '60ms' }}>
        <div className="mb-1 flex items-center justify-between gap-3">
          <h2 className="font-[family-name:var(--font-display)] text-2xl">Passkeys</h2>
          <button
            type="button"
            onClick={() => addPasskey.mutate()}
            disabled={addPasskey.isPending}
            className="btn btn-primary btn-sm"
          >
            <Fingerprint className="h-3.5 w-3.5" aria-hidden />
            Hinzufügen
          </button>
        </div>
        <p className="mb-4 text-sm text-[var(--color-fg3)]">
          Anmeldung per Fingerabdruck, Gesichtserkennung oder Geräte-PIN. Ein Passkey ersetzt die
          Abfrage des Einmalcodes.
        </p>

        {passkeys.isLoading && <Spinner />}

        {passkeys.data?.length === 0 && (
          <EmptyState
            title="Noch kein Passkey"
            description="Auf diesem Gerät einen anlegen — die Anmeldung geht danach ohne Passwort."
          />
        )}

        <ul className="space-y-3">
          {passkeys.data?.map((passkey) => (
            <li
              key={passkey.id}
              className="flex items-center justify-between gap-3 rounded-[var(--radius-md)] border border-[var(--color-line)] px-4 py-3.5"
            >
              <span className="min-w-0">
                <span className="block truncate text-[0.9375rem]">
                  {passkey.name ?? 'Passkey'}
                </span>
                <span className="block text-sm text-[var(--color-fg3)]">
                  angelegt {formatDateTime(passkey.created_at)}
                  {passkey.last_used_at
                    ? ` · zuletzt ${formatDateTime(passkey.last_used_at)}`
                    : ' · noch nicht genutzt'}
                </span>
              </span>
              <button
                type="button"
                onClick={() => removePasskey.mutate(passkey.id)}
                className="btn btn-quiet btn-sm"
                aria-label="Passkey entfernen"
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      </section>

      <button
        type="button"
        onClick={() => void auth.logout()}
        className="btn btn-ghost btn-lg mt-4 w-full"
      >
        <LogOut className="h-4 w-4" aria-hidden />
        Abmelden
      </button>
    </div>
  );
}

/** Grobe Gerätebezeichnung, damit mehrere Passkeys unterscheidbar bleiben. */
function deviceName(): string {
  const agent = navigator.userAgent;
  if (/iPhone|iPad/.test(agent)) return 'iPhone oder iPad';
  if (/Android/.test(agent)) return 'Android-Gerät';
  if (/Mac/.test(agent)) return 'Mac';
  if (/Windows/.test(agent)) return 'Windows-PC';
  return 'Dieses Gerät';
}
